import { intensiveLocateMethod } from '@sb/ai/intensiveLocateMethods';
import type { SkillData } from '@sb/feathers-types';
import type { Step as RcStep } from '@sb/remote-control/types';
import { intensiveAIFeaturesAllowed } from '@sb/service-interfaces/host/processorGate';

import { type ValidationRule, type ValidationContext } from '../../types';
import {
  checkCameraCalibration,
  resolveCameraEquipmentReference,
} from '../../util/cameraEquipment';
import { ActionRequiredError } from '../../util/errors';
import {
  assertNotExamplePlaceholder,
  SKILL_PLACEHOLDER_MESSAGE,
} from '../../util/examplePlaceholder';
import { humanJoin } from '../../util/humanJoin';
import {
  getInvalidLocateSkillFields,
  getMissingLocateSkillConfig,
} from '../../util/locateSkill';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';
import { checkCaptureFileName } from './captureConfigChecks';

const NO_LOCATE_SKILL_MESSAGE = 'No Locate skill selected';

// Blocks RunLocateSkill when no skill is selected, or when the selected skill
// is missing pieces or fails schema validation. A skill that is not one of
// the robot's locate skills counts as none selected.
class RunLocateSkillRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.RunLocateSkill', 'RunLocateSkill');
  }

  protected validate({
    args,
    ctx,
    routine,
    warn,
  }: CanonicalValidateArgs): void {
    const a = args as Extract<
      RcStep.Arguments,
      { argumentKind: 'RunLocateSkill' }
    >;

    if (!a.skillId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: NO_LOCATE_SKILL_MESSAGE,
        fieldId: 'skillId',
      });
    }

    // Before the lookup, which skips a skill it cannot find.
    assertNotExamplePlaceholder(
      a.skillId,
      'skillId',
      SKILL_PLACEHOLDER_MESSAGE,
    );

    if (a.captureConfig) {
      checkCaptureFileName(a.captureConfig, routine, ctx);
    }

    const skill = this.resolveSkill(
      ctx,
      a.skillId,
      'locate',
      NO_LOCATE_SKILL_MESSAGE,
    );

    if (!skill) return;

    warn(this.draftSkillWarning(skill));

    this.checkProcessor(skill.method, ctx);

    const missing = getMissingLocateSkillConfig({
      shared: skill.shared as Record<string, unknown>,
      method: skill.method,
      methodConfig: skill.methodConfig as Record<string, unknown>,
    });

    const labels: string[] = [];
    if (missing.capturePose) labels.push('capture pose');
    if (missing.teach) labels.push('teach configuration');
    if (missing.referencePlane) labels.push('reference plane');

    if (labels.length > 0) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: `This Locate skill needs ${humanJoin(
          labels,
        )} before the routine can run.`,
        fieldId: 'skillId',
      });
    }

    // Check for schema failures (e.g. cleared number inputs) that would cause mid-run "not found" errors.
    const invalidFields = getInvalidLocateSkillFields(skill);

    if (invalidFields.length > 0) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: `This Locate skill has invalid saved configuration (${humanJoin(
          invalidFields,
        )}) and cannot run. Reopen the skill and re-enter those values.`,
        fieldId: 'skillId',
      });
    }

    this.checkCamera(skill, ctx);
  }

  /**
   * The camera the skill captures through has to still be a camera this robot
   * has, calibrated the way locate needs. Mirrors the inline `Locate` step's
   * `checkCameraEquipment`: existence, kind, and both calibrations, the last
   * of which the runtime fails on with "has no extrinsics or intrinsics set".
   *
   * @throws {ActionRequiredError}
   */
  private checkCamera(skill: SkillData, ctx: ValidationContext): void {
    const cameraId = (skill.shared as Record<string, unknown> | undefined)
      ?.cameraId;

    if (typeof cameraId !== 'string' || cameraId.length === 0) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'This Locate skill has no camera selected, so the routine cannot run. Open the skill and pick a camera.',
        fieldId: 'skillId',
      });
    }

    const camera = resolveCameraEquipmentReference(ctx, cameraId, {
      subject: 'this Locate skill',
      openTarget: 'skill',
      fieldId: 'skillId',
    });

    // Undefined only when the caller loaded no equipment — an absent list
    // means "not loaded here", the same way an absent `skills` list does
    // above — so there is nothing whose calibration to check.
    if (!camera) return;

    checkCameraCalibration(camera, {
      subject: 'this Locate skill',
      fieldId: 'skillId',
    });
  }

  /**
   * The learned detectors need a control box above the P-core threshold. A
   * routine carrying one can arrive on a box that cannot run it — authored on
   * capable hardware and copied over, restored from a backup, or played
   * straight through External Control — so refusing it here is what makes the
   * editor's lock an actual gate rather than an affordance.
   *
   * @throws {ActionRequiredError}
   */
  private checkProcessor(method: string, ctx: ValidationContext): void {
    // Omitted means the caller never asked the host; see ValidationContext.
    if (ctx.processorPCores == null) return;

    const intensive = intensiveLocateMethod(method);
    if (!intensive) return;

    if (intensiveAIFeaturesAllowed(ctx.processorPCores)) return;

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `${intensive.label} needs more compute than this control box has, so this Locate skill cannot run here.`,
      fieldId: 'skillId',
    });
  }
}

export const RunLocateSkillValidation: ValidationRule =
  new RunLocateSkillRule().toValidationRule();
